import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Dialog } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import DeleteIconButton from "components/IconButtons/DeleteIconButton";
import EditIconButton from "components/IconButtons/EditIconButton";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import { FormattedMessage as T, useIntl } from "react-intl";

import DeleteEntityDialog from "components/DeleteEntityDialog";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useState } from "react";
import { useQueryClient } from "react-query";
import {
  DomainFilter,
  ListRequestOfDomainFilter,
  ListResponseOfOrganizationApplicationDomain,
  OrganizationApplicationDomain,
  OrganizationApplicationDomainSaveRequest,
  OrganizationDetails,
  ResponseCode,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import OrganizationApplicationForm from "./OrganizationApplicationDomainForm";

type OrganizationApplicationsProps = {
  organization: OrganizationDetails;
};

const useStyles = (theme: Theme) => ({
  applicationsHeader: {
    display: "flex",
    justifyContent: "end",
    margin: theme.spacing(4, 0, 3, 0),
    width: "100%",
  },
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

const OrganizationApplicationDomains = ({
  organization,
}: OrganizationApplicationsProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const { organizationService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("applicationId");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const [
    organizationApplicationDomain,
    setOrganizationApplicationDomain,
  ] = useState<OrganizationApplicationDomain | null>(null);

  const organizationApplicationDomains = useStrictQuery<ListResponseOfOrganizationApplicationDomain>(
    [
      "organization-application-domains",
      organization.id,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      organizationService.getOrganizationApplicationDomains(
        ListRequestOfDomainFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: DomainFilter.fromJS({
            organizationId: organization.id,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  const [createOpen, createOpenActions] = useBoolean(false);

  const cells: Array<TableCellType> = [
    {
      accessor: "applicationName",
      customSortAccessor: "application.Name",
      align: "left",
      type: "text",
      label: t({ id: "applicationNameLabel" }),
    },
    {
      accessor: "hostName",
      align: "left",
      type: "text",
      label: t({ id: "hostNameLabel" }),
    },
    {
      accessor: "environment",
      align: "left",
      type: "text",
      label: t({ id: "environmentLabel" }),
    },
    {
      accessor: "isDefault",
      align: "left",
      type: "boolean",
      label: t({ id: "isDefaultLabel" }),
    },
    {
      accessor: "value",
      align: "left",
      width: 0,
      type: "node",
      noSort: true,
      View: ({ value }: { value: OrganizationApplicationDomain }) => (
        <>
          <EditIconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOrganizationApplicationDomain(value);
              updateOpenActions.setTrue();
            }}
          />
          <DeleteIconButton
            size="small"
            onClick={() => {
              setOrganizationApplicationDomain(value);
              deleteOpenActions.setTrue();
            }}
          />
        </>
      ),
      label: "",
    },
  ];

  const saveOrganizationApplicationDomain = async (
    values: OrganizationApplicationDomainSaveRequest
  ) => {
    try {
      const result = await organizationService.saveOrganizationApplicationDomain(
        OrganizationApplicationDomainSaveRequest.fromJS({
          ...values,
          organizationId: organization.id,
        })
      );

      if (result.responseCode === ResponseCode.OK) {
        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "applicationDomainSaved" }))
        );
        queryClient.invalidateQueries([
          "organization-application-domains",
          organization.id,
        ]);

        cleanup();
      }
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const cleanup = () => {
    updateOpenActions.setFalse();
    createOpenActions.setFalse();
    setOrganizationApplicationDomain(null);
  };

  const deleteOrganizationApplicationDomain = async () => {
    try {
      const result = await organizationService.deleteOrganizationApplicationDomain(
        organizationApplicationDomain?.id!
      );

      if (result.responseCode === ResponseCode.OK) {
        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "applicationDomainDeleted" }))
        );
        queryClient.invalidateQueries([
          "organization-application-domains",
          organization.id,
        ]);
        deleteOpenActions.setFalse();
      }
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Box>
      <LazyTable
        {...{
          rowCount: organizationApplicationDomains.data?.totalRecords ?? 0,
          rows: organizationApplicationDomains.data?.data ?? [],
          loading: organizationApplicationDomains.isFetching,
          titleComponent: (
            <Box sx={styles.applicationsHeader}>
              <Button
                onClick={createOpenActions.setTrue}
                startIcon={<AddIcon />}
              >
                <T id="addApplicationDomain" />
              </Button>
            </Box>
          ),
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <OrganizationApplicationForm
          onSubmit={saveOrganizationApplicationDomain}
          title={t({ id: "addApplicationDomain" })}
          onCancel={cleanup}
        />
      </Dialog>

      <Dialog open={updateOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <OrganizationApplicationForm
          onSubmit={saveOrganizationApplicationDomain}
          organizationApplicationDomain={organizationApplicationDomain}
          title={t({ id: "updateApplicationDomain" })}
          onCancel={cleanup}
        />
      </Dialog>

      <DeleteEntityDialog
        dialogTitleKey="applicationDomainDeleteTitle"
        contentKey="applicationDomainDeleteQuestion"
        open={!!deleteOpen}
        handleDelete={deleteOrganizationApplicationDomain}
        onCancel={() => deleteOpenActions.setFalse()}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default withErrorBoundary(OrganizationApplicationDomains);
